<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue'
import VueScrollTo from 'vue-scrollto'
import { useUserStore } from '@/stores/user'
import InputStep1UserRegistrationForShowContents from '@/components/user/registration/InputStep1UserRegistrationForShowContents.vue'
import InputStep2UserRegistrationForShowContents from '@/components/user/registration/InputStep2UserRegistrationForShowContents.vue'
import InputStep3UserRegistrationForShowContents from '@/components/user/registration/InputStep3UserRegistrationForShowContents.vue'
import InputStep4UserRegistrationForShowContents from '@/components/user/registration/InputStep4UserRegistrationForShowContents.vue'
import { newUserSessionPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'InputUserRegistrationForShowContents',

  components: {
    InputStep1UserRegistrationForShowContents,
    InputStep2UserRegistrationForShowContents,
    InputStep3UserRegistrationForShowContents,
    InputStep4UserRegistrationForShowContents,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    form: {
      type: Object as PropType<Form>,
      required: true,
    },

    userLeadInfo: {
      type: Object as PropType<UserLeadInfo>,
      required: true,
    },

    inputCustomFields: {
      type: Object as PropType<Inputs>,
      required: true,
    },

    customFieldConfigItems: {
      type: Array as PropType<CustomFieldConfig[]>,
      default: () => [],
    },

    inputHearingFields: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },

    additionalStep3Inputs: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },

    hearingFieldConfig: {
      type: Object as PropType<HearingFieldConfig>,
      default: () => ({
        nameSuffix: {
          id: '',
          hearingFieldId: '',
          value: '',
          secondaryValue: '',
        },
        sections: [],
      }),
    },

    inputOptionalNewsletterInfo: {
      type: Object as PropType<OptionalNewsletterSettings>,
      required: true,
    },

    submitButtonCaption: {
      type: String,
      default: '',
    },

    /** 情報提供先の掲載企業名。step4 の submit 直上に表示する同意文言へ渡す */
    agreementCompanyName: {
      type: String,
      default: '',
    },

    step1HeadingTitle: {
      type: String,
      default: 'サクッと1分で登録！',
    },

    step1HeadingDescription: {
      type: String,
      default: '',
    },

    step1SubmitLabel: {
      type: String,
      default: '登録して資料を受け取る(無料)',
    },
  },

  emits: [
    'update:lastName',
    'update:firstName',
    'update:lastNameKana',
    'update:firstNameKana',
    'update:companyName',
    'update:industry',
    'update:scale',
    'update:listingStatus',
    'update:profession',
    'update:professionDomain',
    'open:professionDomain',
    'close:professionDomain',
    'update:positionLevel',
    'update:departmentAndPosition',
    'update:workMailAddress',
    'update:phoneNumber',
    'update:corporateZipcode',
    'update:corporateAddress1',
    'update:corporateAddress2',
    'update:customFields',
    'update:hearingFields',
    'select:companyLeadInfo',
    'update:mailStopFlag',
    'update:mailNewsStopFlag',
    'update:mailExpoStopFlag',
    'update:mailAiStopFlag',
    'select:hearingMultiSelectPrimary',
    'close:hearingMultiSelectSecondary',
  ],

  setup(props, { emit }) {
    // constants
    const MAX_STEP = 4
    const HEADER_HEIGHT = 104 // header 高さ
    const SP_GLOBAL_PROP = 12 // SP 時の画面両脇の隙間（padding）
    const SCROLL_OPTION = {
      offset: -(HEADER_HEIGHT + SP_GLOBAL_PROP), // STEP 切り替わり時のスクロールした際に、セクションの上部とヘッダーがかぶるため、SPのヘッダー分の高さ（ + 若干遊びの隙間分）を offset で減算（PC と SP が同設定値になってしまうが、さほど違和感はないのでこのまま。）
    }

    // store
    const user = useUserStore()
    const currentStep = ref(1)

    /**
     * reCAPTCHA を読み込んでよいか
     *
     * @description reCAPTCHA のスクリプトは 358KB あり、初期表示中にダウンロードすると
     *   その帯域を専有してしまう。実際に必要になるのは STEP4 の送信時なので、
     *   STEP2 へ到達した時点（「次へ」の押下）を起点に読み込みを解禁する。
     * @description 一度解禁したら戻さない。prev() で STEP1 に戻った際にウィジェットごと
     *   アンマウントされると、認証済みトークンを載せた hidden input が form から消えてしまう。
     */
    const isRecaptchaLoadable = ref(false)

    const formElementIdName = computed(() => `form-${props.form.name}`)

    const getStateClassNameByStep = (targetStep: number): string => {
      if (targetStep === currentStep.value) return 'is-current'
      if (targetStep === currentStep.value - 1) return 'is-prev'
      if (targetStep === currentStep.value + 1) return 'is-next'

      return ''
    }

    const next = () => {
      if (currentStep.value < MAX_STEP) currentStep.value++
      isRecaptchaLoadable.value = true
      VueScrollTo.scrollTo('#section-user-lead-info', SCROLL_OPTION)
    }

    const prev = () => {
      if (currentStep.value > 1) currentStep.value--
      VueScrollTo.scrollTo('#section-user-lead-info', SCROLL_OPTION)
    }

    const submit = () => {
      const form = document.getElementById(formElementIdName.value)
      if (form) {
        (form as HTMLFormElement).submit()
      }
    }

    /**
     * ヒアリング項目の「その他」に該当する項目が選択された際の処理
     *
     * @param name 対応する追加設問の name 属性値
     * @param value 対応する追加設問の入力要素オブジェクト
     * @description 引数は emit で通知される値を暗黙的に受け取る
     */
    const onSelectInHearingMultiSelectorPrimary = (name: string, value: Input) => {
      emit('select:hearingMultiSelectPrimary', name, value)
    }

    return {
      // constants
      MAX_STEP,

      // routes
      newUserSessionPath,

      // store
      user,

      // data
      isRecaptchaLoadable,

      // computed
      formElementIdName,

      // methods
      next,
      prev,
      submit,
      getStateClassNameByStep,
      onSelectInHearingMultiSelectorPrimary,
    }
  }
})
</script>

<template>
  <FormDefault
    :id="formElementIdName"
    :name="form.name"
    :action="form.action"
    :method="form.method">
    <section class="f-section__userRegistration">
      <!-- step 1 -->
      <div
        :class="[
          'f-section__userRegistration--body',
          getStateClassNameByStep(1),
        ]">
        <InputStep1UserRegistrationForShowContents
          namespace="userRegistrationStep1"
          :last-name="userLeadInfo.lastName!"
          :first-name="userLeadInfo.firstName!"
          :last-name-kana="userLeadInfo.lastNameKana!"
          :first-name-kana="userLeadInfo.firstNameKana!"
          :work-mail-address="userLeadInfo.workMailAddress!"
          @update:last-name="$emit('update:lastName', $event)"
          @update:first-name="$emit('update:firstName', $event)"
          @update:last-name-kana="$emit('update:lastNameKana', $event)"
          @update:first-name-kana="$emit('update:firstNameKana', $event)"
          @update:work-mail-address="$emit('update:workMailAddress', $event)"
          @click:next="next">
          <template #headingDescription>
            <slot name="step1HeadingDescription" />
          </template>

          <template #submitLabel>
            <slot name="step1SubmitLabel" />
          </template>
        </InputStep1UserRegistrationForShowContents>
      <!-- /.f-section__userRegistration body --></div>

      <!-- step 2 -->
      <div
        :class="[
          'f-section__userRegistration--body',
          getStateClassNameByStep(2),
        ]">
        <InputStep2UserRegistrationForShowContents
          namespace="userRegistrationStep2"
          :company-name="userLeadInfo.companyName!"
          :industry="userLeadInfo.industry!"
          :profession="userLeadInfo.profession!"
          :profession-domain="userLeadInfo.professionDomain!"
          :position-level="userLeadInfo.positionLevel!"
          :department-and-position="userLeadInfo.departmentAndPosition!"
          :corporate-zipcode="userLeadInfo.corporateZipcode!"
          :corporate-address1="userLeadInfo.corporateAddress1!"
          :corporate-address2="userLeadInfo.corporateAddress2!"
          :corporate-number="userLeadInfo.corporateNumber!"
          :corporate-employee-num="userLeadInfo.corporateEmployeeNum!"
          @update:company-name="$emit('update:companyName', $event)"
          @update:industry="$emit('update:industry', $event)"
          @update:profession="$emit('update:profession', $event)"
          @update:profession-domain="$emit('update:professionDomain', $event)"
          @open:profession-domain="$emit('open:professionDomain', $event)"
          @close:profession-domain="$emit('close:professionDomain', $event)"
          @update:position-level="$emit('update:positionLevel', $event)"
          @update:department-and-position="$emit('update:departmentAndPosition', $event)"
          @select:company-lead-info="$emit('select:companyLeadInfo', $event)"
          @click:next="next"
          @click:prev="prev">
          <template #heading>
            <slot name="step2Heading" />
          </template>
        </InputStep2UserRegistrationForShowContents>
      <!-- /.f-section__userRegistration body --></div>

      <!-- step 3 -->
      <div
        :class="[
          'f-section__userRegistration--body',
          getStateClassNameByStep(3),
        ]">
        <InputStep3UserRegistrationForShowContents
          namespace="userRegistrationStep3"
          :scale="userLeadInfo.scale!"
          :listing-status="userLeadInfo.listingStatus!"
          :phone-number="userLeadInfo.phoneNumber!"
          :input-custom-fields="inputCustomFields"
          :custom-field-config-items="customFieldConfigItems"
          :input-hearing-fields="inputHearingFields"
          :hearing-field-config="hearingFieldConfig"
          :additional-inputs="additionalStep3Inputs"
          @update:scale="$emit('update:scale', $event)"
          @update:listing-status="$emit('update:listingStatus', $event)"
          @update:phone-number="$emit('update:phoneNumber', $event)"
          @update:custom-fields="$emit('update:customFields', $event)"
          @update:hearing-fields="$emit('update:hearingFields', $event)"
          @select:hearing-multi-select-primary="onSelectInHearingMultiSelectorPrimary"
          @close:hearing-multi-select-secondary="$emit('close:hearingMultiSelectSecondary', $event)"
          @click:next="next"
          @click:prev="prev">
          <template #heading>
            <slot name="step3Heading" />
          </template>
          <template #additionalInputs="slotProps">
            <slot
              name="step3"
              v-bind="slotProps" />
          </template>
        </InputStep3UserRegistrationForShowContents>
      <!-- /.f-section__userRegistration body --></div>

      <!-- step 4 -->
      <div
        :class="[
          'f-section__userRegistration--body',
          getStateClassNameByStep(4),
        ]">
        <InputStep4UserRegistrationForShowContents
          namespace="userRegistrationStep4"
          :is-recaptcha-loadable="isRecaptchaLoadable"
          :input-optional-newsletter-info="inputOptionalNewsletterInfo"
          :submit-button-caption="submitButtonCaption"
          :agreement-company-name="agreementCompanyName"
          @update:mail-stop-flag="$emit('update:mailStopFlag', $event)"
          @update:mail-news-stop-flag="$emit('update:mailNewsStopFlag', $event)"
          @update:mail-expo-stop-flag="$emit('update:mailExpoStopFlag', $event)"
          @update:mail-ai-stop-flag="$emit('update:mailAiStopFlag', $event)"
          @click:submit="submit" />
      <!-- /.f-section__userRegistration body --></div>
    <!-- /.f-section__userRegistration --></section>

    <slot />
  </FormDefault>
</template>

<style lang="sass" scoped>
// foundation
.f-section__userRegistration
  $root: &
  position: relative
  display: grid
  grid-template-columns: 1fr
  grid-template-rows: auto

  & &--body
    max-height: 500px
    position: relative
    grid-column: 1 / 1
    grid-row: 1 / 1
    opacity: 0
    visibility: hidden
    +transition($duration: 1s)

    &.is-current
      max-height: 9999px
      opacity: 1
      visibility: visible

    &:not(.is-current)
      max-height: 0
      overflow: hidden

// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center

  + .p
    margin-top: 0.5em
</style>
